import PropTypes from 'prop-types';
import { useContext, createContext } from 'react';
import { useSelector } from 'react-redux';

// material-ui
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider } from '@mui/material';

// third-party
import { SnackbarProvider } from 'notistack';

// defaultTheme
import themes from 'themes';

// project imports
import useLocalStorage from 'hooks/useLocalStorage';
import NavigationScroll from 'layout/NavigationScroll';
import { STORAGE_DNDMODE, STORAGE_ANIMATION_TOGGLE } from 'constants/store';

export const UIContext = createContext(null);

export const UIProvider = ({ children }) => {
    const [animation, setAnimation] = useLocalStorage(STORAGE_ANIMATION_TOGGLE, true);
    const [dndmode, setDNDMode] = useLocalStorage(STORAGE_DNDMODE, false);

    const state = {
        dndmode,
        animation,
    };

    const actions = {
        setDNDMode,
        setAnimation,
    };

    const value = {
        ...state,
        ...actions,
    };

    return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = () => {
    const context = useContext(UIContext);
    if (context === undefined) {
        throw new Error(`useUI must be used within a UIProvider`);
    }
    return context;
};

export const ManagedUIContext = ({ children }) => {
    const customization = useSelector((state) => state.customization);

    return (
        <UIProvider>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={themes(customization)}>
                    <CssBaseline />
                    <SnackbarProvider
                        maxSnack={3}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                    >
                        <NavigationScroll>{children}</NavigationScroll>
                    </SnackbarProvider>
                </ThemeProvider>
            </StyledEngineProvider>
        </UIProvider>
    );
};

UIProvider.propTypes = { children: PropTypes.node };
ManagedUIContext.propTypes = { children: PropTypes.node };
